import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, Sparkles } from 'lucide-react';
import { aaiApi } from '../services/aaiApi';
import { useAsync } from './aai/useAsync';
import type { OrbitRoomSummary } from '../types/aai';
import { Loading, RouteMark, type RouteKind } from '../components/aa';

/*
 * Your workspace is ready: what a company sees the moment setup finishes.
 *
 * It reports a DRAFT, not a running programme. The setup created a company and
 * a first Orbit cycle for the owner to review; no source is connected, no
 * invitation is sent, and nothing has entered Company Context. The four tiles
 * say so plainly rather than showing encouraging zeroes.
 *
 * The cycle is read, never created here: `current` would author a draft on a
 * page load, and a confirmation screen has no business authoring anything.
 */

const NEXT_STEPS = [
  {
    title: 'Review the questions',
    desc: 'Change anything that is unclear or unnecessary.',
  },
  {
    title: 'Choose who takes part',
    desc: 'Start with yourself or invite named people.',
  },
  {
    title: 'Review findings',
    desc: 'Only approved findings can become company Context or opportunities.',
  },
];

const StateTile: React.FC<{
  kind?: RouteKind;
  glyph?: string;
  kicker: string;
  title: string;
  desc: string;
}> = ({ kind, glyph, kicker, title, desc }) => (
  <div className="rounded-panel border border-line bg-white px-5 py-4">
    <div className="flex items-start gap-3.5">
      <RouteMark kind={kind} glyph={glyph} size="md" />
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{kicker}</div>
        <div className="mt-0.5 text-[15px] font-bold text-ink">{title}</div>
        <p className="m-0 mt-0.5 text-[12.5px] leading-[18px] text-ink-dim">{desc}</p>
      </div>
    </div>
  </div>
);

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{label}</div>
    <div className="mt-1 text-[13px] font-bold text-ink">{value}</div>
  </div>
);

const WorkspaceReady: React.FC = () => {
  const navigate = useNavigate();
  const name = localStorage.getItem('user_name') || 'You';
  const company = localStorage.getItem('client_name') || '';

  const room = useAsync<OrbitRoomSummary>(
    () => aaiApi.getOrbitRoomSummary(),
    'Could not load what Orbit knows so far.',
    [],
  );
  const questions = room.data?.open || 0;
  const known = (room.data?.known || 0) + (room.data?.confirm || 0);

  return (
    <div className="min-h-screen bg-canvas">
      <header className="mx-auto flex max-w-[1240px] flex-wrap items-center justify-between gap-4 px-6 py-7 md:px-10">
        <img src="/logo-black.png" alt="Agents Anywhere" className="h-7 w-auto object-contain" />
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="inline-flex h-10 items-center gap-2 rounded-act border border-line bg-white px-4 text-[13px] font-semibold text-ink whitespace-nowrap"
          >
            Preview roles
            <ChevronDown size={14} />
          </button>
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-card bg-accent-500 text-[13px] font-bold text-black">
              {name.charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="truncate text-[13.5px] font-bold text-ink">{name}</div>
              <div className="truncate text-[12px] text-ink-dim">
                Provisional setup owner · role pending verification
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1240px] px-6 pb-16 pt-10 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            {company && (
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
                {company}
              </div>
            )}
            <h1 className="m-0 mt-2 text-[40px] font-bold leading-[1.06] tracking-[-0.03em] text-ink sm:text-[48px]">
              Your workspace is ready
            </h1>
            <p className="m-0 mt-3 text-[15px] leading-[24px] text-ink-muted">
              Orbit read your setup into the company file. Confirm what it found before anyone is invited.
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 rounded-act bg-accent-100 px-4 py-2.5 text-[13px] font-semibold text-ink">
            <Check size={15} strokeWidth={3} />
            Draft created
          </span>
        </div>

        <div className="mt-8 rounded-panel border border-line bg-white px-6 py-6 md:px-8">
          {room.loading && <Loading label="Reading what Orbit knows" />}
          {!room.loading && (
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="flex min-w-0 flex-1 items-start gap-5">
                <span className="grid h-[54px] w-[54px] shrink-0 place-items-center rounded-card bg-accent-500 text-black">
                  <Sparkles size={26} strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">
                    Your next step
                  </div>
                  <div className="mt-1 text-[22px] font-bold leading-tight text-ink">
                    Confirm what Orbit already knows
                  </div>
                  <p className="m-0 mt-1.5 text-[13.5px] leading-[21px] text-ink-dim">
                    Orbit asks only what it could not read itself. Everything else is already on file for you to confirm.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-x-10 gap-y-4">
                    <Fact label="On file" value={`${known} facts`} />
                    <Fact label="Still to ask" value={`${questions} questions`} />
                    <Fact label="Ways to respond" value="Text, voice or a document" />
                    <Fact label="Access" value="No apps connected" />
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 flex-col gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/orbit/intake')}
                  className="inline-flex h-[50px] items-center justify-center gap-2.5 rounded-act bg-ink px-6 text-[14px] font-semibold text-white transition-colors hover:bg-black whitespace-nowrap"
                >
                  Review Orbit setup <ArrowRight size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex h-[50px] items-center justify-center rounded-act border border-line-strong bg-white px-6 text-[14px] font-semibold text-ink transition-colors hover:bg-canvas-inset whitespace-nowrap"
                >
                  Enter platform
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {/* Projects and AI adoption carry the platform's own route marks. A
              route mark means the same thing on every screen, so Context and
              Opportunities take a plain glyph rather than borrowing one. */}
          <StateTile glyph="C" kicker="Company Context" title="Starting" desc="No findings approved yet" />
          <StateTile glyph="O" kicker="Opportunities" title="None yet" desc="Orbit will show why each is suggested" />
          <StateTile kind="project" kicker="Projects" title="None yet" desc="Start only after a business review" />
          <StateTile kind="adoption" kicker="AI adoption" title="Not started" desc="Prepared when roles and work are clear" />
        </div>

        <div className="mt-10 border-t border-line pt-6">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
            How the first cycle works
          </div>
          <div className="mt-4 grid gap-5 lg:grid-cols-3">
            {NEXT_STEPS.map((step, i) => (
              <div key={step.title} className="flex items-start gap-3.5 rounded-card bg-canvas-inset px-5 py-4">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent-500 font-sans text-[11px] font-bold text-black">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <div className="text-[13.5px] font-bold text-ink">{step.title}</div>
                  <p className="m-0 mt-0.5 text-[12.5px] leading-[18px] text-ink-dim">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default WorkspaceReady;
